import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { reconciliationResultMessages } from "@/services/messages";
import { X } from "lucide-react";

/**
 * Панель фильтров отчёта.
 *
 * Содержит:
 * - search-input с иконкой и × для сброса (debounce обрабатывается в хуке).
 * - CTA «Сбросить фильтры» — рендерится только когда есть активный поиск.
 *
 * Фича 026: убраны chip-toggle severity и level. После унификации pipeline
 * level стал внутренней классификацией Discrepancy (один отчёт = оба уровня),
 * фильтр теряет смысл. Severity-чипы удалены вслед — на практике пользователь
 * фильтрует через master-detail UI и поиск.
 *
 * Sprint 5 / B1: убран CTA «Восстановить скрытое» — client-side mute удалён.
 */
export interface ResultFilterBarProps {
	searchQuery: string;
	isFiltering: boolean;
	onSearchChange: (query: string) => void;
	onReset: () => void;
	/** Внешний ref на search input — нужен для keyboard-shortcut «/» (фича 024). */
	searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

export function ResultFilterBar({
	searchQuery,
	isFiltering,
	onSearchChange,
	onReset,
	searchInputRef,
}: ResultFilterBarProps) {
	const messages = reconciliationResultMessages.filter;
	return (
		<section
			className="rounded-lg border border-slate-200 bg-white px-4 py-3"
			aria-label={messages.search_placeholder}
		>
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative min-w-[16rem] flex-1">
					<SearchInput
						ref={searchInputRef}
						value={searchQuery}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder={messages.search_placeholder}
						className={searchQuery.length > 0 ? "pr-8" : undefined}
					/>
					{searchQuery.length > 0 && (
						<button
							type="button"
							onClick={() => onSearchChange("")}
							aria-label={messages.search_clear_aria}
							className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>

				{isFiltering && (
					<Button type="button" variant="outline" size="sm" onClick={onReset}>
						{messages.reset_cta}
					</Button>
				)}
			</div>
		</section>
	);
}
